// ETicketPage.tsx — /e-ticket/:pnr. Full boarding-pass card e-ticket view.
// TODO(eticket-api-PR): Replace ETICKETS mock with real GET /bookings/:pnr/eticket.

import { useParams, useNavigate } from 'react-router-dom';
import { notYetWiredToast } from '@/components/primitives';
import { ETICKETS } from '@/lib/trips-mock';

const F = '"Inter Variable","Inter",system-ui,sans-serif';
const DISPLAY = '"Fraunces Variable","Fraunces",Georgia,serif';

// Deterministic barcode: vary bar widths by cycling widths seeded from pnr characters
function BarcodeStripes({ pnr }: { pnr: string }) {
  const widths = [2, 1, 4, 1, 3, 1, 2, 4, 1, 2, 3, 1, 2, 1, 4, 2, 1, 3, 1, 2, 4, 1, 3, 2, 1, 3, 1, 2];
  const seed = pnr.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const bars: { w: number; isBlack: boolean }[] = [];
  let toggle = true;
  for (let i = 0; i < 60; i++) {
    bars.push({ w: widths[(i + seed) % widths.length] ?? 2, isBlack: toggle });
    toggle = !toggle;
  }
  return (
    <div style={{ display: 'flex', height: 64, alignItems: 'stretch' }}>
      {bars.map((bar, i) => (
        <div
          key={i}
          style={{
            width: bar.w,
            background: bar.isBlack ? '#1B3A6B' : 'transparent',
          }}
        />
      ))}
    </div>
  );
}

// Static QR-like placeholder (3×3 finder + data blocks)
function QRPlaceholder({ pnr }: { pnr: string }) {
  const size = 72;
  const cell = size / 9;
  const seed = pnr.split('').reduce((a, c) => a + c.charCodeAt(0), 13);
  const grid = Array.from({ length: 9 }, (_, r) =>
    Array.from({ length: 9 }, (_, c) => {
      // corner finders
      if ((r < 3 && c < 3) || (r < 3 && c > 5) || (r > 5 && c < 3)) return true;
      return ((r * 9 + c + seed) % 3) !== 0;
    }),
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {grid.map((row, r) =>
        row.map((filled, c) =>
          filled ? (
            <rect
              key={`${r}-${c}`}
              x={c * cell}
              y={r * cell}
              width={cell - 1}
              height={cell - 1}
              fill="#1B3A6B"
              rx={1}
            />
          ) : null,
        ),
      )}
    </svg>
  );
}

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{
        fontFamily: F, fontSize: 10, letterSpacing: 1.5, fontWeight: 700,
        color: '#8C98B0', textTransform: 'uppercase', marginBottom: 3,
      }}>{label}</div>
      <div style={{ fontFamily: F, fontSize: 15, fontWeight: 700, color: '#1B3A6B' }}>{value}</div>
    </div>
  );
}

export function ETicketPage() {
  const { pnr } = useParams<{ pnr: string }>();
  const navigate = useNavigate();

  const ticket = pnr ? ETICKETS[pnr] : null;

  if (!ticket) {
    return (
      <div style={{
        background: '#0A0F1C', minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16, color: '#F4F3F0', textAlign: 'center',
      }}>
        <div style={{ fontSize: 48 }}>✈</div>
        <h2 style={{ fontFamily: F, fontSize: 28, fontWeight: 700, margin: 0 }}>Ticket not found</h2>
        <p style={{ fontFamily: F, fontSize: 15, color: '#8C98B0', margin: 0 }}>PNR <strong>{pnr}</strong> doesn't exist in our records.</p>
        <button
          onClick={() => navigate('/trips')}
          style={{
            marginTop: 8, height: 48, padding: '0 24px', borderRadius: 999,
            background: '#E8441A', color: '#fff', border: 'none', cursor: 'pointer',
            fontFamily: F, fontSize: 14, fontWeight: 700,
          }}
        >← Back to my trips</button>
      </div>
    );
  }

  return (
    <div style={{ background: '#0A0F1C', minHeight: '100vh', padding: '48px 0 80px' }}>
      {/* Dark aurora bg */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <div style={{
          position: 'absolute', top: -100, left: -100, width: 500, height: 500,
          background: 'radial-gradient(circle, rgba(232,68,26,0.15) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}/>
        <div style={{
          position: 'absolute', bottom: -100, right: -100, width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(75,142,219,0.12) 0%, transparent 65%)',
          filter: 'blur(60px)',
        }}/>
      </div>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, margin: '0 auto', padding: '0 24px' }}>
        {/* Back nav */}
        <button
          onClick={() => navigate('/trips')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: F, fontSize: 14, fontWeight: 600, color: '#8C98B0',
            marginBottom: 28, padding: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8C98B0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M5 12l7-7M5 12l7 7"/>
          </svg>
          My trips
        </button>

        {/* Page title */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: F, fontSize: 11, letterSpacing: 2.5, color: '#8C98B0', fontWeight: 600, marginBottom: 8 }}>
            BOARDING PASS
          </div>
          <h1 style={{
            fontFamily: DISPLAY,
            fontSize: 'clamp(40px,6vw,56px)',
            fontWeight: 500,
            color: '#F4F3F0',
            margin: 0,
            lineHeight: 0.95,
            letterSpacing: -0.03,
          }}>
            Have a great<br/>
            <em style={{ fontStyle: 'italic' }}>flight.</em>
          </h1>
        </div>

        {/* Boarding pass card */}
        <div style={{
          background: '#FAF7F2',
          borderRadius: 28,
          boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
          overflow: 'hidden',
          color: '#1B3A6B',
        }}>
          {/* Airline header strip */}
          <div style={{
            background: ticket.airlineColor,
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: F, fontSize: 13, fontWeight: 800, color: '#fff',
              }}>{ticket.airlineCode}</div>
              <div>
                <div style={{ fontFamily: F, fontSize: 14, fontWeight: 700, color: '#fff' }}>{ticket.airline}</div>
                <div style={{ fontFamily: F, fontSize: 11, color: 'rgba(255,255,255,0.75)', letterSpacing: 1, marginTop: 2 }}>
                  {ticket.flightNumber} · {ticket.aircraft}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: F, fontSize: 10, color: 'rgba(255,255,255,0.65)', letterSpacing: 1.5, fontWeight: 600 }}>PNR</div>
              <div style={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 500, color: '#fff', letterSpacing: 2 }}>
                {ticket.pnr}
              </div>
            </div>
          </div>

          {/* Route section */}
          <div style={{ padding: '28px 28px 24px', borderBottom: '2px dashed #D3CDC1' }}>
            {/* Big route */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <div>
                <div style={{ fontFamily: DISPLAY, fontSize: 72, fontWeight: 500, lineHeight: 0.85, letterSpacing: -0.04, color: '#1B3A6B' }}>
                  {ticket.from}
                </div>
                <div style={{ fontFamily: F, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: '#8C98B0', marginTop: 8 }}>
                  {ticket.fromCity.toUpperCase()}
                </div>
              </div>

              <div style={{ flex: 1, padding: '0 20px', textAlign: 'center' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <div style={{ flex: 1, height: 1.5, background: 'repeating-linear-gradient(90deg, #1B3A6B 0, #1B3A6B 5px, transparent 5px, transparent 10px)' }}/>
                  <div style={{ padding: '0 8px', flexShrink: 0 }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1B3A6B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.72A2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81"/>
                      <path d="M3 3l18 9-9 9-4-4"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, height: 1.5, background: 'repeating-linear-gradient(90deg, #1B3A6B 0, #1B3A6B 5px, transparent 5px, transparent 10px)' }}/>
                </div>
                <div style={{ fontFamily: F, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#8C98B0', marginTop: 8 }}>
                  {ticket.duration}
                </div>
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: DISPLAY, fontSize: 72, fontWeight: 500, lineHeight: 0.85, letterSpacing: -0.04, color: '#1B3A6B' }}>
                  {ticket.to}
                </div>
                <div style={{ fontFamily: F, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: '#8C98B0', marginTop: 8 }}>
                  {ticket.toCity.toUpperCase()}
                </div>
              </div>
            </div>

            {/* Times */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 22 }}>
              <div>
                <div style={{ fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: '#8C98B0', textTransform: 'uppercase' }}>Depart</div>
                <div style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 500, color: '#1B3A6B', lineHeight: 1, marginTop: 4 }}>{ticket.depart}</div>
                <div style={{ fontFamily: F, fontSize: 12, color: '#8C98B0', marginTop: 4 }}>{ticket.departDate} · {ticket.terminal}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: '#8C98B0', textTransform: 'uppercase' }}>Arrive</div>
                <div style={{ fontFamily: DISPLAY, fontSize: 32, fontWeight: 500, color: '#1B3A6B', lineHeight: 1, marginTop: 4 }}>{ticket.arrive}</div>
                <div style={{ fontFamily: F, fontSize: 12, color: '#8C98B0', marginTop: 4 }}>{ticket.toCity}</div>
              </div>
            </div>
          </div>

          {/* Notch cutouts */}
          <div style={{ position: 'relative', height: 0 }}>
            <div style={{ position: 'absolute', left: -12, top: -12, width: 24, height: 24, borderRadius: '50%', background: '#0A0F1C' }}/>
            <div style={{ position: 'absolute', right: -12, top: -12, width: 24, height: 24, borderRadius: '50%', background: '#0A0F1C' }}/>
          </div>

          {/* Passenger + flight info grid */}
          <div style={{ padding: '28px 28px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20, borderBottom: '2px dashed #D3CDC1' }}>
            <MetaField label="Passenger" value={ticket.passengers[0] ?? '—'} />
            <MetaField label="Seat" value={ticket.seat} />
            <MetaField label="Class" value={ticket.cabinClass.toUpperCase()} />
            <MetaField label="Gate" value={ticket.gate} />
            <MetaField label="Boarding" value={ticket.boardingTime} />
            <MetaField label="Terminal" value={ticket.terminal} />
          </div>

          {/* Barcode + QR section */}
          <div style={{ padding: '24px 28px 28px', display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ flex: 1 }}>
              <BarcodeStripes pnr={ticket.pnr} />
              <div style={{ fontFamily: F, fontSize: 10, letterSpacing: 2, color: '#8C98B0', textAlign: 'center', marginTop: 10, fontWeight: 600 }}>
                ETKT {ticket.pnr} · {ticket.flightNumber}
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <QRPlaceholder pnr={ticket.pnr} />
            </div>
          </div>
        </div>

        {/* Action row */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginTop: 24,
          flexWrap: 'wrap',
        }}>
          {[
            {
              label: 'Download PDF',
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
              ),
              feature: 'PDF download',
            },
            {
              label: 'Add to calendar',
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <path d="M16 2v4M8 2v4M3 10h18"/>
                </svg>
              ),
              feature: 'Calendar integration',
            },
            {
              label: 'Share via WhatsApp',
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.72A2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 14 14 0 003.12 6.4 14 14 0 006.4 3.12c.37.09.74.16 1.12.21"/>
                  <path d="M15 11c.79-.79 1.5-1.5 2-2"/>
                </svg>
              ),
              feature: 'WhatsApp share',
            },
          ].map(action => (
            <button
              key={action.label}
              onClick={() => notYetWiredToast(action.feature)}
              style={{
                flex: 1,
                minWidth: 0,
                height: 48,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#F4F3F0',
                fontFamily: F,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
